import { Button, Dialog, DialogButtons, Modal } from '@st/theme'
import type { ReactNode } from 'react'
import { ViewerSidebarContent, ViewerSidebarTabs } from '~/st-doc-viewer/sidebar-layout'
import { match, P } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { TaxReturnDeliveryFieldEditor } from './field-editor-sidebar'
import { SchemaEditor } from '~/st-doc-viewer/schema-editor/view'
import type { Schema } from '~/st-doc-viewer/schema-editor/model'
import { jurisdictionControl } from './jurisdiction-control'
import type { ContractEditorMessage } from '~/features/contract-editing/editor'
import {
  selTaxReturnDeliveryEditor,
  selTaxReturnDeliveryPolicy,
  type TaxReturnDeliveryMessage
} from './state'

export function TaxReturnDeliverySidebar({ pageGroupEditor }: { pageGroupEditor: ReactNode }) {
  const folder = useProcess('stFolder')
  const sendDelivery = (message: TaxReturnDeliveryMessage) =>
    folder.send({ type: 'taxReturnDelivery', message })
  const sendContract = (message: ContractEditorMessage) =>
    sendDelivery({ type: 'contract', message })
  const delivery = useSelect('stFolder', (state) => {
    if (state.status != 'ready') throw new Error('Delivery editor requires a ready folder')
    return state.taxReturnDelivery
  })
  const { saveState, canEdit, canSave, hasSummary } = useSelect(
    'stFolder',
    selTaxReturnDeliveryPolicy
  )
  const schema = useSelect('stFolder', (state) => {
    if (state.status != 'ready') throw new Error('Summary requires a ready folder')
    const document = state.folderState.documents.find((doc) => doc.id == state.selectedDocId)!
    return state.folderState.dataSchemas[document.documentTypeId!] as Schema
  })
  const summaryEditor = useSelect('stFolder', selTaxReturnDeliveryEditor)
  const label = delivery.editor.type
  const catalog = match(delivery.editor)
    .with({ type: 'summary' }, ({ catalog }) => catalog ?? [])
    .otherwise(() => [])
  const confirmingRemoval =
    delivery.phase == 'active' &&
    delivery.editor.type == 'signing' &&
    delivery.editor.draft.interaction.type == 'confirmingRemoval'

  return (
    <>
      <ViewerSidebarContent
        tabs={
          <ViewerSidebarTabs
            tabs={[
              ...match(hasSummary)
                .with(true, () => [{ id: 'summary' as const, label: 'Summary' }])
                .with(false, () => [])
                .exhaustive(),
              { id: 'signing' as const, label: 'Signing' },
              { id: 'bookmarks' as const, label: 'Bookmarks' }
            ]}
            selected={match(delivery.editor.type)
              .with('creating', () => 'signing' as const)
              .otherwise((type) => type)}
            disabled={!canEdit}
            onSelect={(tab) => sendDelivery({ type: 'tabSelected', tab })}
          />
        }
        footer={
          delivery.editor.type !== 'bookmarks' && (
            <div className="flex flex-col gap-2">
              <span role="status" className="text-xs text-stone-500 empty:hidden">
                {match(saveState)
                  .with('saving', () => 'Saving changes…')
                  .with('dirty', () => 'Unsaved changes')
                  .with('saved', () => null)
                  .exhaustive()}
              </span>
              <Button
                variant="primary"
                className="w-full whitespace-nowrap"
                disabled={!canSave}
                onClick={() => sendDelivery({ type: 'saveRequested' })}
              >
                {match(saveState)
                  .with('saving', () => 'Saving…')
                  .with(P.union('dirty', 'saved'), () =>
                    match(delivery.editor.type)
                      .with('summary', () => 'Update Summary Page')
                      .otherwise(() => 'Save changes')
                  )
                  .exhaustive()}
              </Button>
            </div>
          )
        }
      >
        <div role="tabpanel" aria-label={label}>
          <fieldset disabled={!canEdit} className="min-w-0">
            {match(delivery.editor.type)
              .with('creating', () => null)
              .with('bookmarks', () => pageGroupEditor)
              .with('summary', () => (
                <SchemaEditor
                  // Remounting on lock closes any portaled menu the editor opened.
                  key={String(canEdit)}
                  schema={schema}
                  state={summaryEditor}
                  controls={{ jurisdiction: jurisdictionControl(catalog) }}
                  onEdit={(edit) => sendDelivery({ type: 'summaryEdited', edit })}
                />
              ))
              .with('signing', () => <TaxReturnDeliveryFieldEditor />)
              .exhaustive()}
          </fieldset>
        </div>
      </ViewerSidebarContent>
      <Modal
        isOpen={confirmingRemoval}
        onClose={() => sendContract({ type: 'signerRemoveCancelled' })}
      >
        <Dialog
          title="Remove spouse?"
          subtitle="The spouse and any signature and date fields assigned to them will be removed from this draft."
          buttons={
            <DialogButtons>
              <Button
                variant="default"
                onClick={() => sendContract({ type: 'signerRemoveCancelled' })}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => sendContract({ type: 'signerRemoveConfirmed' })}
              >
                Remove spouse
              </Button>
            </DialogButtons>
          }
        />
      </Modal>
    </>
  )
}
